import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, TrendingUp } from "lucide-react";
import { StudyTimeStats } from "@/hooks/useReportsData";

interface StudyTimeCardProps {
  studyTime: StudyTimeStats;
}

function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

const ALL_ACTIVITIES = ["Flashcards", "Quizzes", "Exams"] as const;

export const StudyTimeCard: React.FC<StudyTimeCardProps> = ({ studyTime }) => {
  // Build complete breakdown with all activities
  const completeBreakdown = ALL_ACTIVITIES.map((activity) => {
    const existing = studyTime.breakdown.find((b) => b.activity === activity);
    if (existing) {
      return existing;
    }
    // Get minutes from studyTime based on activity
    let minutes = 0;
    if (activity === "Flashcards") minutes = studyTime.flashcardsMinutes;
    else if (activity === "Quizzes") minutes = studyTime.quizzesMinutes;
    else if (activity === "Exams") minutes = studyTime.examsMinutes;
    
    return {
      activity,
      minutes,
      percentage: studyTime.totalMinutes > 0 
        ? Math.round((minutes / studyTime.totalMinutes) * 100) 
        : 0,
    };
  });

  return (
    <Card className="flex h-full flex-col border-border bg-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          <Clock className="h-5 w-5 text-primary" />
          Study Time
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col space-y-4">
        {/* Total Time */}
        <div className="flex items-baseline justify-between">
          <span className="text-3xl font-bold text-foreground">
            {formatTime(studyTime.totalMinutes)}
          </span>
          <span className="text-sm text-muted-foreground">total</span>
        </div>

        {/* Weekly Stats */}
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3">
          <TrendingUp className="h-4 w-4 text-green-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {formatTime(studyTime.weeklyMinutes)} this week
            </p>
            <p className="text-xs text-muted-foreground">
              ~{formatTime(studyTime.dailyAverage)} daily average
            </p>
          </div>
        </div>

        {/* Activity Breakdown - Always show all activities */}
        <div className="flex-1 space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Breakdown
          </p>
          {completeBreakdown.map((item) => (
            <div key={item.activity} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground">{item.activity}</span>
                <span className="text-muted-foreground">
                  {formatTime(item.minutes)} ({item.percentage}%)
                </span>
              </div>
              <Progress
                value={item.percentage}
                className="h-1.5"
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
